const ISSUE_KEY_BY_CODE = Object.freeze({
  CATEGORY_EMPTY: 'categories_empty',
  PRODUCT_INVALID_PRICE: 'products_invalid_price',
  OPTION_MISSING_VALUES: 'options_missing_values',
  OPTION_INVALID_MIN: 'options_invalid_min'
})

const ISSUE_GROUP_LABELS = Object.freeze({
  categories_empty: 'gestiune.integrationsMenuValidationCategoriesEmpty',
  products_invalid_price: 'gestiune.integrationsMenuValidationProductsInvalidPrice',
  options_missing_values: 'gestiune.integrationsMenuValidationOptionsMissingValues',
  options_invalid_min: 'gestiune.integrationsMenuValidationOptionsInvalidMin'
})

function getItemTypeLabel (code, t) {
  const c = String(code || '').toUpperCase()
  if (c.startsWith('CATEGORY')) return t('gestiune.integrationsMenuValidationItemCategory')
  if (c.startsWith('PRODUCT')) return t('gestiune.integrationsMenuValidationItemProduct')
  if (c.startsWith('OPTION')) return t('gestiune.integrationsMenuValidationItemOption')
  return ''
}

export function translateMenuValidationErrorCode (code, t) {
  const normalized = code == null ? '' : String(code).trim()
  if (!normalized) return ''

  const codeKey = `gestiune.integrationsMenuValidationErrorCodes.${normalized}`
  const codeLabel = t(codeKey)
  if (codeLabel !== codeKey) return codeLabel

  const issueKey = ISSUE_KEY_BY_CODE[normalized]
  if (issueKey) {
    return t(ISSUE_GROUP_LABELS[issueKey])
  }

  return ''
}

function getErrorGroupLabel (entry, t) {
  if (!entry || typeof entry !== 'object') {
    return t('gestiune.integrationsGlovoValidationErrors')
  }

  const fromCode = translateMenuValidationErrorCode(entry.code, t)
  if (fromCode) return fromCode

  if (entry.code) return String(entry.code).trim()

  return t('gestiune.integrationsGlovoValidationErrors')
}

export function formatMenuValidationItem (item, t) {
  if (item == null) return ''
  if (typeof item === 'string') return item.trim()
  if (typeof item !== 'object') return String(item).trim()

  const typeLabel = getItemTypeLabel(item.code, t)
  const prefix = typeLabel ? `[${typeLabel}] ` : ''
  const id = item.id != null && item.id !== '' ? item.id : null
  const name = item.name != null ? String(item.name).trim() : ''

  if (id != null && name) return `${prefix}#${id} — ${name}`
  if (id != null) return `${prefix}#${id}`
  if (name) return `${prefix}${name}`
  if (item.message) return String(item.message).trim()
  return ''
}

function parseEditTargetFromLegacyMessage (text) {
  const raw = String(text || '').trim()
  if (!raw) return null

  const idMatch = raw.match(/\bid:\s*'?(\d+)'?/i) || raw.match(/#(\d+)/)
  const id = idMatch ? String(idMatch[1]) : null
  if (!id) return null

  const entityMatch = raw.match(/entity\s+'([^']+)'/i)
  const entity = entityMatch ? String(entityMatch[1]).toLowerCase() : ''

  if (entity.includes('group')) {
    return { type: 'group-option', id }
  }
  if (entity.includes('product') && !entity.includes('option')) {
    return { type: 'product', id }
  }
  if (entity.includes('option')) {
    return { type: 'product-option', id }
  }

  return null
}

export function resolveMenuValidationEditTarget (item) {
  if (item == null) return null
  if (typeof item === 'string') return parseEditTargetFromLegacyMessage(item)
  if (typeof item !== 'object') return null

  const optionId = item.company_option_id != null && item.company_option_id !== ''
    ? String(item.company_option_id)
    : null
  if (optionId) {
    return { type: 'product-option', id: optionId }
  }

  const id = item.id != null && item.id !== '' ? String(item.id) : null
  const code = String(item.code || '').toUpperCase()
  const entity = String(item.entity || item.entity_type || '').toLowerCase()

  if (id) {
    if (code.startsWith('PRODUCT') || entity === 'products' || entity === 'product') {
      return { type: 'product', id }
    }
    if (entity.includes('group') || code.includes('GROUP')) {
      return { type: 'group-option', id }
    }
    if (code.startsWith('OPTION') || entity.includes('option')) {
      return { type: 'product-option', id }
    }
  }

  if (item.message) return parseEditTargetFromLegacyMessage(item.message)
  return null
}

function normalizeMenuValidationEntry (item, t) {
  const text = formatMenuValidationItem(item, t)
  if (!text) return null
  return {
    text,
    editTarget: resolveMenuValidationEditTarget(item)
  }
}

export function getMenuValidationGroupEntries (group, t) {
  if (!group || typeof group !== 'object') return []
  if (Array.isArray(group.entries) && group.entries.length) {
    return group.entries
  }
  const source = Array.isArray(group.items) ? group.items : []
  return source
    .map((item) => {
      if (item && typeof item === 'object' && item.text) {
        return {
          text: String(item.text).trim(),
          editTarget: item.editTarget || resolveMenuValidationEditTarget(item)
        }
      }
      return normalizeMenuValidationEntry(item, t)
    })
    .filter(Boolean)
}

function normalizeLegacyValidationList (value) {
  const items = []
  const collect = (entry) => {
    if (entry == null) return
    if (Array.isArray(entry)) {
      entry.forEach(collect)
      return
    }
    if (typeof entry === 'object') {
      if (Array.isArray(entry.items) && entry.items.length) {
        entry.items.forEach(collect)
        return
      }
      if (entry.message) collect(entry.message)
      else if (entry.error) collect(entry.error)
      return
    }
    const text = String(entry).trim()
    if (text) items.push(text)
  }
  collect(value)
  return [...new Set(items)]
}

/**
 * When apiRequest catches HTTP 4xx/5xx, the body is under error.response.data.
 */
export function getAxiosErrorResponseData (response) {
  if (!response || typeof response !== 'object') return null
  const data = response.error && response.error.response && response.error.response.data
  if (data && typeof data === 'object') return data
  return null
}

/**
 * Prefer the real API payload when the request failed with an HTTP error status.
 */
export function unwrapMenuApiResponse (response) {
  if (!response || typeof response !== 'object') return response
  const nested = getAxiosErrorResponseData(response)
  if (!nested) return response
  return {
    ...nested,
    status: nested.status !== undefined ? nested.status : false,
    error: response.error
  }
}

function isMenuEmptyErrorCode (code) {
  return String(code || '').trim().toUpperCase() === 'MENU_EMPTY'
}

function textIndicatesMenuNotFound (text) {
  const message = String(text || '').trim().toLowerCase()
  if (!message) return false
  return message.includes('menu not found') ||
    message.includes('no exportable products') ||
    message.includes('menu is empty') ||
    message.includes('menu has no exportable') ||
    message.includes('no products to export') ||
    /\bno products\b/.test(message)
}

function collectErrorEntries (errors) {
  if (errors == null) return []
  if (Array.isArray(errors)) return errors
  if (typeof errors === 'object') return [errors]
  return [errors]
}

function responseHasMenuEmptyCode (payload) {
  if (!payload || typeof payload !== 'object') return false
  const lists = [
    ...collectErrorEntries(payload.errors),
    ...collectErrorEntries(payload.data && payload.data.errors)
  ]
  return lists.some((entry) => {
    if (!entry || typeof entry !== 'object') return false
    return isMenuEmptyErrorCode(entry.code)
  })
}

/**
 * Detect empty / missing menu payloads from Glovo, Bolt, and Wolt validate/export.
 */
export function isMenuNotFoundResponse (response) {
  if (!response) return false

  const unwrapped = unwrapMenuApiResponse(response)
  if (responseHasMenuEmptyCode(unwrapped) || responseHasMenuEmptyCode(response)) {
    return true
  }

  const message = extractApiResponseMessage(response)
  if (textIndicatesMenuNotFound(message)) return true

  // Glovo returnMenuJson shell: { products: [], errors: { message: "Menu not found" } }
  const errors = unwrapped && unwrapped.errors
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    if (textIndicatesMenuNotFound(errors.message) || textIndicatesMenuNotFound(errors.error)) {
      return true
    }
  }

  return false
}

function getStructuredErrorsSource (response) {
  if (!response || typeof response !== 'object') return []
  if (Array.isArray(response.errors) && response.errors.length) {
    return response.errors
  }
  // Glovo menu pull: errors is `{ message: "Menu not found" }`, not an array
  if (response.errors && typeof response.errors === 'object' && !Array.isArray(response.errors)) {
    if (response.errors.code || response.errors.message || response.errors.error) {
      return [response.errors]
    }
  }
  const data = response.data
  if (data && Array.isArray(data.errors) && data.errors.length) {
    return data.errors
  }
  if (data && data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    if (data.errors.code || data.errors.message || data.errors.error) {
      return [data.errors]
    }
  }
  return []
}

function buildMenuEmptyValidationGroup (t) {
  const label = translateMenuValidationErrorCode('MENU_EMPTY', t) ||
    t('gestiune.integrationsGlovoMenuEmpty')
  return {
    code: 'MENU_EMPTY',
    label,
    entries: [{ text: label, editTarget: null }]
  }
}

function buildGroupsFromStructuredErrors (errors, t) {
  const groups = []
  errors.forEach((entry) => {
    if (entry == null) return

    if (typeof entry === 'string' || typeof entry === 'number') {
      const text = String(entry).trim()
      if (!text) return
      if (textIndicatesMenuNotFound(text) || isMenuEmptyErrorCode(text)) {
        groups.push(buildMenuEmptyValidationGroup(t))
        return
      }
      groups.push({
        code: '',
        label: t('gestiune.integrationsGlovoValidationErrors'),
        entries: [{ text, editTarget: null }]
      })
      return
    }

    if (typeof entry !== 'object') return

    if (isMenuEmptyErrorCode(entry.code) || textIndicatesMenuNotFound(entry.message)) {
      groups.push(buildMenuEmptyValidationGroup(t))
      return
    }

    const label = getErrorGroupLabel(entry, t)
    const entries = (Array.isArray(entry.items) ? entry.items : [])
      .map(item => normalizeMenuValidationEntry(item, t))
      .filter(Boolean)
    if (entries.length) {
      groups.push({
        code: entry.code || '',
        label,
        entries
      })
    } else if (entry.message) {
      groups.push({
        code: entry.code || '',
        label,
        entries: [{ text: String(entry.message).trim(), editTarget: null }]
      })
    } else if (label) {
      groups.push({
        code: entry.code || '',
        label,
        entries: [{ text: label, editTarget: null }]
      })
    }
  })
  return groups
}

function buildGroupsFromIssues (response, t) {
  const issues = response && response.data && response.data.issues
  if (!issues || typeof issues !== 'object') return []

  const groups = []
  Object.keys(ISSUE_GROUP_LABELS).forEach((key) => {
    const list = issues[key]
    if (!Array.isArray(list) || !list.length) return
    const entries = list
      .map(item => normalizeMenuValidationEntry(item, t))
      .filter(Boolean)
    if (!entries.length) return
    groups.push({
      code: key,
      label: t(ISSUE_GROUP_LABELS[key]),
      entries
    })
  })
  return groups
}

export function extractMenuValidationGroups (response, t) {
  const source = unwrapMenuApiResponse(response)
  if (isMenuNotFoundResponse(response)) {
    return [buildMenuEmptyValidationGroup(t)]
  }

  const structured = getStructuredErrorsSource(source)
  const fromErrors = buildGroupsFromStructuredErrors(structured, t)
  if (fromErrors.length) return fromErrors

  const fromIssues = buildGroupsFromIssues(source, t)
  if (fromIssues.length) return fromIssues

  const legacyItems = []
  if (source && source.errors != null) {
    legacyItems.push(...normalizeLegacyValidationList(source.errors))
  }
  const payload = source && source.data
  if (payload) {
    legacyItems.push(...normalizeLegacyValidationList(payload.errors))
    legacyItems.push(...normalizeLegacyValidationList(payload.details))
    if (payload.provider_response) {
      legacyItems.push(...normalizeLegacyValidationList(payload.provider_response.errors))
      legacyItems.push(...normalizeLegacyValidationList(payload.provider_response.details))
    }
  }
  const uniqueLegacy = [...new Set(legacyItems)]
  if (uniqueLegacy.length) {
    if (uniqueLegacy.some(textIndicatesMenuNotFound)) {
      return [buildMenuEmptyValidationGroup(t)]
    }
    return [{
      code: '',
      label: t('gestiune.integrationsGlovoValidationErrors'),
      entries: uniqueLegacy
        .map(item => normalizeMenuValidationEntry(item, t))
        .filter(Boolean)
    }]
  }
  return []
}

export function extractMenuValidationWarnings (response, t) {
  const items = []
  const payload = response && response.data
  if (payload) {
    items.push(...normalizeLegacyValidationList(payload.warnings))
    if (payload.provider_response) {
      items.push(...normalizeLegacyValidationList(payload.provider_response.warnings))
    }
  }
  return [...new Set(items)]
}

export function isMenuValidationSuccessful (response, t, isMenuNotFound) {
  if (isMenuNotFound) return false
  if (!response || !response.status || response.status === false || response.error) {
    return false
  }

  const rootData = response.data
  const payload = rootData && (rootData.data || rootData)

  if (rootData && rootData.valid === false) return false
  if (payload && payload.valid === false) return false
  if (payload && payload.is_valid === false) return false

  return extractMenuValidationGroups(response, t).length === 0
}

/** Explicit overrides apply to reported issues, not missing menus or transport errors. */
export function canProceedWithMenuValidation (response, t) {
  if (!response || isMenuNotFoundResponse(response)) return false
  if (response.error) {
    const httpResponse = response.error.response
    if (!httpResponse || httpResponse.status >= 500) return false
  }
  return extractMenuValidationGroups(response, t).length > 0
}

export function getMenuValidationSummary (response, success, t, isMenuNotFound) {
  if (isMenuNotFound) {
    return t('gestiune.integrationsGlovoMenuEmpty')
  }
  if (success) return ''
  if (extractMenuValidationGroups(response, t).length) {
    return t('gestiune.integrationsGlovoValidationFailedTitle')
  }
  if (response && response.message) return String(response.message).trim()
  return t('gestiune.integrationsActionError')
}

export function getMenuValidationEntityEditPath (target) {
  if (!target || !target.type || target.id == null || target.id === '') return null

  const paths = {
    product: `/integrations/menu-fix/product/${target.id}`,
    'product-option': `/integrations/menu-fix/product-option/${target.id}`,
    'group-option': `/integrations/menu-fix/group-option/${target.id}`
  }

  return paths[target.type] || null
}

export function getMenuValidationShortMessage (response, t, isMenuNotFound) {
  if (extractMenuValidationGroups(response, t).length) {
    return ''
  }
  return getMenuValidationSummary(response, false, t, isMenuNotFound)
}

function extractMessageFromUnknownErrorValue (value, depth = 0) {
  if (value == null || value === '' || depth > 5) return ''

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return ''
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(trimmed)
        const nested = extractMessageFromUnknownErrorValue(parsed, depth + 1)
        if (nested) return nested
      } catch (e) {
        // keep plain-text fallback below
      }
    }
    return trimmed
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => extractMessageFromUnknownErrorValue(item, depth + 1))
      .filter(Boolean)
      .join('\n')
  }

  if (typeof value === 'object') {
    if (value.message != null && String(value.message).trim()) {
      return String(value.message).trim()
    }

    const nestedKeys = ['error', 'errors', 'provider_response', 'data', 'details']
    for (let i = 0; i < nestedKeys.length; i += 1) {
      const nested = extractMessageFromUnknownErrorValue(value[nestedKeys[i]], depth + 1)
      if (nested) return nested
    }
  }

  return ''
}

export function extractApiResponseMessage (response) {
  if (!response) return ''

  if (typeof response === 'string') {
    return extractMessageFromUnknownErrorValue(response)
  }

  if (typeof response !== 'object') {
    return String(response).trim()
  }

  // Prefer the API body over axios's generic "Request failed with status code NNN"
  const nestedData = getAxiosErrorResponseData(response)
  if (nestedData) {
    const nestedMessage = extractApiResponseMessage(nestedData)
    if (nestedMessage) return nestedMessage
  }

  const directMessage = extractMessageFromUnknownErrorValue(response.message)
  if (directMessage) return directMessage

  const errorsMessage = extractMessageFromUnknownErrorValue(response.errors)
  if (errorsMessage) return errorsMessage

  if (response.data != null) {
    const dataMessage = extractMessageFromUnknownErrorValue(response.data)
    if (dataMessage) return dataMessage
  }

  // Fall back to axios/network error message only when no API body message exists
  const axiosMessage = extractMessageFromUnknownErrorValue(response.error)
  if (axiosMessage) return axiosMessage

  return ''
}

const PLATFORM_HTTP_ERROR_PATTERN = /Client error:|Server error:|resulted in a `\d{3}/i
const PLATFORM_CODE_PATTERN = /NOT_AUTHORIZED|UNAUTHORIZED|INVALID_TOKEN|FORBIDDEN|NOT_FOUND/i
const PLATFORM_NETWORK_PATTERN = /cURL error|Connection refused|timed out|Could not resolve host|Failed to connect/i

function parseHttpStatusFromPlatformMessage (message) {
  const match = String(message || '').match(/resulted in a `(\d{3})/i)
  return match ? parseInt(match[1], 10) : null
}

function parsePlatformCodeFromMessage (message) {
  const text = String(message || '')
  const jsonMatch = text.match(/\{[\s\S]*\}/)
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0])
      if (parsed && parsed.message != null && String(parsed.message).trim()) {
        return String(parsed.message).trim().toUpperCase()
      }
    } catch (e) {
      // fall through to pattern matching
    }
  }
  const codeMatch = text.match(PLATFORM_CODE_PATTERN)
  return codeMatch ? codeMatch[0].toUpperCase() : ''
}

function looksLikePlatformHttpError (message) {
  const text = String(message || '').trim()
  if (!text) return false
  return PLATFORM_HTTP_ERROR_PATTERN.test(text) ||
    PLATFORM_CODE_PATTERN.test(text) ||
    PLATFORM_NETWORK_PATTERN.test(text)
}

function integrationPlatformErrorKey (integration, suffix) {
  const integrationKey = String(integration || '').toLowerCase()
  if (integrationKey === 'bolt' || integrationKey === 'glovo' || integrationKey === 'wolt') {
    return `gestiune.integrationsPlatformErrors.${suffix}_${integrationKey}`
  }
  return `gestiune.integrationsPlatformErrors.${suffix}`
}

function integrationFieldLabel (field, translate) {
  return translate(`gestiune.integrationsFields.${field}`)
}

function getPlatformErrorInterpolation (integration, suffix, translate) {
  const integrationKey = String(integration || '').toLowerCase()

  if (integrationKey === 'bolt') {
    if (suffix === 'unauthorized') {
      return {
        secretKey: integrationFieldLabel('secret_key', translate),
        integratorId: integrationFieldLabel('integrator_id', translate),
        providerId: integrationFieldLabel('provider_id', translate)
      }
    }
    if (suffix === 'forbidden' || suffix === 'not_found') {
      return {
        providerId: integrationFieldLabel('provider_id', translate),
        apiUrl: integrationFieldLabel('api_url', translate)
      }
    }
  }

  if (integrationKey === 'glovo') {
    if (suffix === 'unauthorized' || suffix === 'forbidden' || suffix === 'not_found') {
      return {
        apiKey: integrationFieldLabel('api_key', translate),
        storeId: integrationFieldLabel('store_id', translate),
        apiUrl: integrationFieldLabel('api_url', translate)
      }
    }
  }

  if (integrationKey === 'wolt') {
    if (suffix === 'unauthorized' || suffix === 'forbidden' || suffix === 'not_found') {
      return {
        apiKey: integrationFieldLabel('api_key', translate),
        clientId: integrationFieldLabel('client_id', translate),
        venueId: integrationFieldLabel('venue_id', translate),
        apiUrl: integrationFieldLabel('api_url', translate)
      }
    }
  }

  return {}
}

function translatePlatformError (integration, suffix, translate) {
  const interpolation = getPlatformErrorInterpolation(integration, suffix, translate)
  const specificKey = integrationPlatformErrorKey(integration, suffix)
  const specificMessage = translate(specificKey, interpolation)
  if (specificMessage && specificMessage !== specificKey) {
    return specificMessage
  }
  const genericKey = `gestiune.integrationsPlatformErrors.${suffix}`
  const genericMessage = translate(genericKey, interpolation)
  if (genericMessage && genericMessage !== genericKey) {
    return genericMessage
  }
  return ''
}

/**
 * Turn raw aggregator API / Guzzle errors into user-facing copy.
 * Returns the original message when it already looks human-readable.
 */
export function formatIntegrationPlatformError (rawMessage, integration, action, t) {
  const message = String(rawMessage || '').trim()
  const translate = typeof t === 'function' ? t : (key) => key
  const actionKey = ['toggleLocation', 'pushMenu', 'syncSchedule'].includes(action)
    ? action
    : 'generic'

  if (!message) {
    return translate(`gestiune.integrationsPlatformErrors.${actionKey}_failed`) ||
      translate('gestiune.integrationsActionError')
  }

  if (!looksLikePlatformHttpError(message)) {
    return message
  }

  const httpStatus = parseHttpStatusFromPlatformMessage(message)
  const platformCode = parsePlatformCodeFromMessage(message)
  const isUnauthorized = httpStatus === 401 ||
    platformCode === 'NOT_AUTHORIZED' ||
    platformCode === 'UNAUTHORIZED' ||
    platformCode === 'INVALID_TOKEN' ||
    /\b401\b/.test(message) ||
    /unauthorized/i.test(message)

  if (isUnauthorized) {
    return translatePlatformError(integration, 'unauthorized', translate) ||
      translate('gestiune.integrationsPlatformErrors.unauthorized')
  }

  if (httpStatus === 403 || platformCode === 'FORBIDDEN' || /\b403\b/.test(message)) {
    return translatePlatformError(integration, 'forbidden', translate) ||
      translate('gestiune.integrationsPlatformErrors.forbidden')
  }

  if (httpStatus === 404 || platformCode === 'NOT_FOUND' || /\b404\b/.test(message)) {
    return translatePlatformError(integration, 'not_found', translate) ||
      translate('gestiune.integrationsPlatformErrors.not_found')
  }

  if (httpStatus === 429 || /\b429\b/.test(message) || /rate limit/i.test(message)) {
    return translate('gestiune.integrationsPlatformErrors.rate_limited')
  }

  if (PLATFORM_NETWORK_PATTERN.test(message)) {
    return translate('gestiune.integrationsPlatformErrors.network')
  }

  if ((httpStatus != null && httpStatus >= 500) || /\b5\d{2}\b/.test(message)) {
    return translate('gestiune.integrationsPlatformErrors.unavailable')
  }

  return translate(`gestiune.integrationsPlatformErrors.${actionKey}_failed`) ||
    translate('gestiune.integrationsActionError')
}

export function getIntegrationActionErrorMessage (response, integration, action, t) {
  const translate = typeof t === 'function' ? t : (key) => key
  if (isMenuNotFoundResponse(response)) {
    return translate('gestiune.integrationsGlovoMenuEmpty')
  }
  const rawMessage = extractApiResponseMessage(response)
  if (!rawMessage) {
    return translate('gestiune.integrationsActionError')
  }
  return formatIntegrationPlatformError(rawMessage, integration, action, t)
}



// WEBPACK FOOTER //
// ./src/components/Dashboard/Views/Integrations/integrationMenuValidationHelpers.js